import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Maximize, CheckCircle, ArrowRight, Building2, Truck, CalendarDays, Clock } from 'lucide-react';
import { getRentalMachines } from '../services/apiService';
import SEO from '../components/SEO';

const OptionBadge = ({ active, icon: Icon, label }) => (
  <div
    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-semibold border ${
      active
        ? 'border-amber-300 bg-amber-50 text-gray-800'
        : 'border-gray-200 bg-gray-50 text-gray-400 line-through'
    }`}
    data-testid={`rental-option-${label}-${active ? 'yes' : 'no'}`}
  >
    <Icon className="w-4 h-4" />
    <span>{label}</span>
  </div>
);

const Location = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMachines = async () => {
      try {
        setLoading(true);
        const data = await getRentalMachines();
        setMachines(data);
      } catch (error) {
        console.error('Error fetching rental machines:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMachines();
  }, []);

  return (
    <>
      <SEO
        title="Location de machines laser — AKM Laser"
        description="Location de machines laser CO2, fibre et mixtes. Sur place dans notre atelier ou sur site chez vous. Court ou long terme. Tarifs sur devis."
        keywords="location machine laser, louer laser, location laser CO2, location laser fibre, location courte durée, location longue durée, AKM Laser"
      />
      <div className="min-h-screen pt-20" data-testid="location-page">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Location de machines laser</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Besoin d'une machine laser pour un projet ponctuel ou sur la durée ?<br />
              Nous louons nos machines en atelier ou directement sur votre site.
            </p>
          </div>
        </section>

        {/* Options générales */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ color: '#5C3A21' }}>
              Nos formules de location
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Building2, title: 'Sur place', text: 'Dans notre atelier à Moidieu. Accès à la machine sous supervision possible.' },
                { icon: Truck, title: 'Sur site', text: 'Nous livrons et installons la machine dans vos locaux.' },
                { icon: Clock, title: 'Court terme', text: 'Location ponctuelle : à la journée, à la semaine ou au mois.' },
                { icon: CalendarDays, title: 'Long terme', text: 'Engagement de plusieurs mois avec tarifs dégressifs.' },
              ].map((o, idx) => (
                <div key={idx} className="p-6 rounded-2xl border border-amber-100 hover:shadow-xl transition-all" style={{ backgroundColor: '#F5EBDD' }}>
                  <o.icon className="w-10 h-10 mb-4" style={{ color: '#5C3A21' }} />
                  <h3 className="text-lg font-bold mb-2" style={{ color: '#5C3A21' }}>{o.title}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{o.text}</p>
                </div>
              ))}
            </div>
            <p className="text-center mt-10 text-gray-600">
              <strong>Tarifs sur devis</strong> — chaque demande est étudiée selon la machine, la durée et le type de location.
            </p>
          </div>
        </section>

        {/* Machines */}
        <section className="py-16" style={{ backgroundColor: '#FBF6EE' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center" style={{ color: '#5C3A21' }}>
              Machines disponibles à la location
            </h2>

            {loading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#5C3A21' }}></div>
                <p className="text-gray-600">Chargement...</p>
              </div>
            ) : machines.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-amber-100">
                <p className="text-gray-600 text-lg mb-4">Aucune machine en location pour le moment.</p>
                <p className="text-gray-500">
                  Contactez-nous directement pour nous parler de votre besoin.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center mt-6 px-6 py-3 rounded-lg text-white font-semibold"
                  style={{ backgroundColor: '#5C3A21' }}
                  data-testid="location-empty-contact"
                >
                  Nous contacter
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {machines.map((m) => (
                  <div
                    key={m.id}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-amber-100"
                    data-testid={`rental-card-${m.id}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img
                        src={m.image}
                        alt={m.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 p-2"
                      />
                    </div>
                    <div className="p-8">
                      <div className="flex items-start justify-between mb-4">
                        <h3 className="text-2xl font-bold" style={{ color: '#5C3A21' }}>{m.name}</h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            m.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {m.status === 'available' ? 'Disponible' : 'Louée'}
                        </span>
                      </div>

                      {m.description && (
                        <p className="text-gray-700 mb-5 leading-relaxed whitespace-pre-line">{m.description}</p>
                      )}

                      <div className="grid grid-cols-2 gap-4 mb-5">
                        <div className="p-3 rounded-lg" style={{ backgroundColor: '#F5EBDD' }}>
                          <div className="flex items-center space-x-2 mb-1">
                            <Zap className="w-4 h-4" style={{ color: '#5C3A21' }} />
                            <span className="font-semibold text-xs" style={{ color: '#5C3A21' }}>Puissance</span>
                          </div>
                          <p className="text-gray-800 font-bold">{m.power}</p>
                        </div>
                        <div className="p-3 rounded-lg" style={{ backgroundColor: '#F5EBDD' }}>
                          <div className="flex items-center space-x-2 mb-1">
                            <Maximize className="w-4 h-4" style={{ color: '#5C3A21' }} />
                            <span className="font-semibold text-xs" style={{ color: '#5C3A21' }}>Zone de travail</span>
                          </div>
                          <p className="text-gray-800 font-bold">{m.workArea}</p>
                        </div>
                      </div>

                      {/* Options location */}
                      <div className="mb-5">
                        <h4 className="font-semibold mb-3 text-sm text-gray-900">Formules proposées :</h4>
                        <div className="flex flex-wrap gap-2">
                          <OptionBadge active={m.availableAtWorkshop} icon={Building2} label="Sur place" />
                          <OptionBadge active={m.availableOnSite} icon={Truck} label="Sur site" />
                          <OptionBadge active={m.shortTerm} icon={Clock} label="Court terme" />
                          <OptionBadge active={m.longTerm} icon={CalendarDays} label="Long terme" />
                        </div>
                      </div>

                      {m.features && m.features.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-semibold mb-3 text-sm text-gray-900">Inclus :</h4>
                          <ul className="space-y-2">
                            {m.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#5C3A21' }} />
                                <span className="text-gray-700 text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-5 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          <strong>Tarif</strong> sur devis
                        </p>
                        <Link
                          to={`/contact?subject=location&message=${encodeURIComponent('Bonjour,\nJe souhaite obtenir un devis pour la location de la machine : ' + m.name + '.\n\nDurée envisagée : \nType : (sur place / sur site)\n\nMerci.')}`}
                          className="inline-flex items-center px-5 py-2.5 rounded-lg text-white font-semibold shadow-md hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                          style={{ backgroundColor: '#5C3A21' }}
                          data-testid={`rental-quote-btn-${m.id}`}
                        >
                          Demander un devis
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Une question sur la location ?</h2>
            <p className="text-lg text-gray-300 mb-8">
              Nous étudions chaque demande au cas par cas pour proposer la formule la plus adaptée à votre projet.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 rounded-xl text-white font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all"
              style={{ backgroundColor: '#5C3A21' }}
            >
              Nous contacter
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Location;
